import jsPDF from 'jspdf';
import { parseTextForPDF } from './textParser';
import { startTimer, endTimer, monitorLatexCache } from './performanceMonitor';

const latexImageCache = new Map<string, string>();
let mathFieldPool: any[] = [];

const latexToImage = (latex: string): Promise<string> => {
  if (latexImageCache.has(latex)) {
    return Promise.resolve(latexImageCache.get(latex)!);
  }
  return Promise.reject(`LaTeX not pre-rendered: ${latex}`);
};

const createMathFieldPool = (size: number = 5) => {
  for (let i = 0; i < size; i++) {
    const container = document.createElement('div');
    container.style.cssText = 'position:absolute;visibility:hidden;z-index:-1;top:-9999px';
    const mf = document.createElement('math-field') as any;
    mf.readOnly = true;
    container.appendChild(mf);
    document.body.appendChild(container);
    mathFieldPool.push({ container, mf, inUse: false });
  }
};

const getMathField = () => {
  let field = mathFieldPool.find(f => !f.inUse);
  if (!field) {
    const container = document.createElement('div');
    container.style.cssText = 'position:absolute;visibility:hidden;z-index:-1;top:-9999px';
    const mf = document.createElement('math-field') as any;
    mf.readOnly = true;
    container.appendChild(mf);
    document.body.appendChild(container);
    field = { container, mf, inUse: false };
    mathFieldPool.push(field);
  }
  field.inUse = true;
  return field;
};

const releaseMathField = (field: any) => {
  field.inUse = false;
  field.mf.value = '';
};

export const primeLatexCache = async (latexStrings: string[]) => {
  const uniqueLatex = [...new Set(latexStrings.filter(Boolean))];
  if (uniqueLatex.length === 0) return Promise.resolve([]);
  
  startTimer('latex-cache-priming');
  
  if (mathFieldPool.length === 0) {
    createMathFieldPool();
  }
  
  const batchSize = 3;
  const results = [];
  
  for (let i = 0; i < uniqueLatex.length; i += batchSize) {
    const batch = uniqueLatex.slice(i, i + batchSize);
    const batchPromises = batch.map(latex => {
      if (latexImageCache.has(latex)) {
        return Promise.resolve();
      }
      
      return new Promise<void>((resolve, reject) => {
        const field = getMathField();
        const { mf } = field;
        
        try {
          mf.value = latex;
          
          const checkRender = () => {
            try {
              const canvas = mf.shadowRoot?.querySelector('canvas');
              if (canvas && canvas.width > 0 && canvas.height > 0) {
                const image = canvas.toDataURL('image/png');
                latexImageCache.set(latex, image);
                releaseMathField(field);
                resolve();
              } else {
                setTimeout(checkRender, 50);
              }
            } catch (e) {
              releaseMathField(field);
              reject(e);
            }
          };
          
          setTimeout(checkRender, 100);
        } catch (e) {
          releaseMathField(field);
          reject(e);
        }
      });
    });
    
    const batchResults = await Promise.allSettled(batchPromises);
    results.push(...batchResults);
  }
  
  const renderTime = endTimer('latex-cache-priming');
  monitorLatexCache(latexImageCache.size, renderTime);
  
  return results;
};

export const cleanupMathFieldPool = () => {
  mathFieldPool.forEach(({ container }) => {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  });
  mathFieldPool = [];
  latexImageCache.clear();
};


const renderTextWithMath = async (text: string, pdf: jsPDF, x: number, y: number, maxWidth: number, lineHeight: number) => {
  const regex = /(\$\$.*?\$\$|\$.*?\$)/g;
  let lastIndex = 0;
  let currentX = x;
  let currentY = y;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const textPart = text.substring(lastIndex, match.index);
    if (textPart) {
      const textLines = pdf.splitTextToSize(textPart, maxWidth - (currentX - x));
      for (let i = 0; i < textLines.length; i++) {
        if (i > 0) {
          currentY += lineHeight;
          currentX = x;
        }
        pdf.text(textLines[i], currentX, currentY);
        currentX += pdf.getStringUnitWidth(textLines[i]) * pdf.getFontSize() / pdf.internal.scaleFactor;
      }
    }

    const latex = match[0];
    const latexContent = latex.slice(latex.startsWith('$$') ? 2 : 1, latex.endsWith('$$') ? -2 : -1);

    if (latexContent) {
      try {
        const imgData = await latexToImage(latexContent);
        const img = new Image();
        img.src = imgData;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          setTimeout(reject, 5000);
        });

        const imgHeight = (pdf.getFontSize() * 1.2) / 2.83;
        const imgWidth = (img.width * imgHeight) / img.height;

        if (currentX + imgWidth > x + maxWidth) {
          currentY += lineHeight;
          currentX = x;
        }
        pdf.addImage(imgData, 'PNG', currentX, currentY - imgHeight + 2, imgWidth, imgHeight);
        currentX += imgWidth + 1;

      } catch (error) {
        console.warn(`Failed to render LaTeX: ${latexContent}`, error);
        const errorText = `[${latexContent}]`;
        pdf.text(errorText, currentX, currentY);
        currentX += pdf.getStringUnitWidth(errorText) * pdf.getFontSize() / pdf.internal.scaleFactor;
      }
    }
    lastIndex = regex.lastIndex;
  }

  const remainingText = text.substring(lastIndex);
  if (remainingText) {
    const textLines = pdf.splitTextToSize(remainingText, maxWidth - (currentX - x));
    for (let i = 0; i < textLines.length; i++) {
      if (i > 0) {
        currentY += lineHeight;
        currentX = x;
      }
      pdf.text(textLines[i], currentX, currentY);
      currentX += pdf.getStringUnitWidth(textLines[i]) * pdf.getFontSize() / pdf.internal.scaleFactor;
    }
  }

  return currentY;
};

export const generateDetailedAnalysisPDF = async (results: any, test: any, onProgress: (progress: number) => void, onComplete: () => void) => {
  startTimer('pdf-generation');
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    const lineHeight = 6;
    let y = margin;

    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(test.name, pageWidth / 2, y, { align: 'center' });
    y += lineHeight * 2;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Score: ${results.score}/${test.totalMarks}`, margin, y);
    pdf.text(`Accuracy: ${results.accuracy}%`, pageWidth / 2, y, { align: 'center' });
    pdf.text(`Date: ${new Date(results.attempted_at || Date.now()).toLocaleDateString()}`, pageWidth - margin, y, { align: 'right' });
    y += lineHeight * 2;

    const questions = test.questions || [];
    const totalQuestions = questions.length;
    const chunkSize = 5; // Process 5 questions at a time

    for (let i = 0; i < totalQuestions; i += chunkSize) {
      const chunk = questions.slice(i, i + chunkSize);
      for (let j = 0; j < chunk.length; j++) {
        const questionIndex = i + j;
        const q = chunk[j];
        const userAnswer = results.answers[questionIndex] !== undefined ? results.answers[questionIndex] : results.answers[q.id];
        const isCorrect = userAnswer === q.answer;
        const isSkipped = userAnswer === undefined || userAnswer === null;

        if (y > pageHeight - 60) {
          pdf.addPage();
          y = margin;
        }

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Question ${questionIndex + 1}`, margin, y);
        
        const status = isSkipped ? 'Skipped' : isCorrect ? 'Correct (+4)' : 'Incorrect (-1)';
        const statusColor = isSkipped ? [128, 128, 128] : isCorrect ? [0, 128, 0] : [255, 0, 0];
        pdf.setTextColor(...statusColor);
        pdf.text(status, pageWidth - margin, y, { align: 'right' });
        pdf.setTextColor(0, 0, 0);
        y += lineHeight * 1.5;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        y = await renderTextWithMath(q.text, pdf, margin, y, maxWidth, lineHeight) + lineHeight;

        for (let k = 0; k < q.options.length; k++) {
          const option = q.options[k];
          const prefix = `${String.fromCharCode(65 + k)}. `;
          const isCorrectOption = option === q.answer;
          const isUserChoice = option === userAnswer;

          if (isCorrectOption) {
            pdf.setTextColor(0, 128, 0);
            pdf.setFont('helvetica', 'bold');
          } else if (isUserChoice && !isCorrectOption) {
            pdf.setTextColor(255, 0, 0);
            pdf.setFont('helvetica', 'bold');
          } else {
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal');
          }
          
          y = await renderTextWithMath(prefix + option, pdf, margin + 5, y, maxWidth - 5, lineHeight) + lineHeight;
        }
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'normal');
        y += lineHeight / 2;

        const userAnswerText = isSkipped ? 'Not Answered' : userAnswer.toString();
        pdf.text(`Your Answer: ${userAnswerText}`, margin, y);
        pdf.text(`Correct Answer: ${q.answer}`, margin + maxWidth / 2, y);
        y += lineHeight * 1.5;

        if (q.solution) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Explanation:', margin, y);
          y += lineHeight;
          pdf.setFont('helvetica', 'normal');
          y = await renderTextWithMath(q.solution, pdf, margin, y, maxWidth, lineHeight) + lineHeight;
        }
        
        y += lineHeight;
      }

      // Report progress
      const progress = Math.round(((i + chunk.length) / totalQuestions) * 100);
      onProgress(progress);

      // Yield to the main thread
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    const fileName = `${test.name.replace(/[^a-z0-9]/gi, '_')}_Analysis.pdf`;
    pdf.save(fileName);
    
    const generationTime = endTimer('pdf-generation');
    console.log(`PDF generated successfully in ${generationTime.toFixed(2)}ms:`, fileName);

  } catch (error) {
    endTimer('pdf-generation');
    console.error("Failed to generate PDF:", error);
    alert('Failed to generate PDF. Please try again.');
  } finally {
    onComplete();
  }
};


export const generateCertificatePDF = async (user: any, test: any, results: any) => {
  const pdf = new jsPDF('l', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  pdf.setFillColor(245, 245, 245);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  pdf.setDrawColor(59, 130, 246);
  pdf.setLineWidth(3);
  pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);
  
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(59, 130, 246);
  pdf.text('CERTIFICATE OF COMPLETION', pageWidth / 2, 40, { align: 'center' });
  
  pdf.setFontSize(16);
  pdf.setTextColor(100, 100, 100);
  pdf.text('This is to certify that', pageWidth / 2, 60, { align: 'center' });
  
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text(user.name || user.fullName || 'Student', pageWidth / 2, 80, { align: 'center' });
  
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'normal');
  pdf.text('has successfully completed the test', pageWidth / 2, 100, { align: 'center' });
  
  const testNameText = parseTextForPDF(test.name);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(59, 130, 246);
  pdf.text(`"${testNameText}"`, pageWidth / 2, 120, { align: 'center' });
  
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  const scorePercentage = test.totalMarks > 0 ? Math.round((results.score/test.totalMarks)*100) : 0;
  pdf.text(`Score: ${results.score}/${test.totalMarks} (${scorePercentage}%)`, pageWidth / 2, 140, { align: 'center' });
  pdf.text(`Accuracy: ${results.accuracy}%`, pageWidth / 2, 155, { align: 'center' });
  
  pdf.setFontSize(12);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`Date: ${new Date(results.attempted_at || Date.now()).toLocaleDateString()}`, pageWidth / 2, 175, { align: 'center' });
  
  pdf.text('TestSeries - Professional Exam Practice Portal', pageWidth / 2, pageHeight - 20, { align: 'center' });
  
  pdf.save(`${testNameText.replace(/[^a-z0-9]/gi, '_')}_Certificate.pdf`);
};

export const generatePDFFromTestData = async (testData: any, type: 'analysis' | 'certificate', user?: any) => {
  try {
    if (type === 'analysis') {
      console.warn("Direct analysis PDF generation from test data is deprecated. Use generateDetailedAnalysisPDF instead.");
    } else if (type === 'certificate' && user) {
      const totalMarks = testData.totalMarks || (testData.no_q ? testData.no_q * 4 : 0);
      const formattedTest = {
        ...testData,
        totalMarks
      };
      const formattedResults = {
        ...testData,
        score: testData.score || 0,
        accuracy: testData.accuracy || 0,
        attempted_at: testData.attempted_at || Date.now()
      };
      await generateCertificatePDF(user, formattedTest, formattedResults);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};